import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  ArrowLeft,
  Save,
  Trash2,
  Edit3,
  Video,
  CheckCircle,
  Loader2,
  BookOpen,
  Tag,
  Link as IconLink,
  AlertCircle,
  GripVertical,
  Globe,
  Lock,
  Layers, // Ikon untuk Kelas
} from "lucide-react";

export default function KelolaMateri() {
  const [materiList, setMateriList] = useState([]);
  const [mapelList, setMapelList] = useState([]);
  const [loading, setLoading] = useState(true);

  // STATE IZIN AKSES
  const [canEdit, setCanEdit] = useState(false);

  // FORM STATE
  const [formData, setFormData] = useState({
    judul: "",
    link_url: "",
    mata_pelajaran: "",
    jenis: "Materi",
    is_public: "true",
    kelas: "", // TAMBAHAN: State untuk Kelas
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // UI STATE
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: null,
    title: "",
  });

  // Opsi Kelas (Sesuaikan dengan kebutuhan sekolah)
  const opsiKelas = [
    "TK",
    "1 SD",
    "2 SD",
    "3 SD",
    "4 SD",
    "5 SD",
    "6 SD",
    "7 SMP",
    "8 SMP",
    "9 SMP",
    "10 SMA",
    "11 SMA",
    "12 SMA",
    "Umum",
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification((prev) => ({ ...prev, show: false })),
      3000
    );
  };

  async function fetchInitialData() {
    setLoading(true);
    try {
      // 1. Cek User & Role
      const localUser = JSON.parse(localStorage.getItem("ruangKelasUser"));
      const {
        data: { session },
      } = await supabase.auth.getSession();

      let role = localUser?.role || "";

      if (session) {
        const { data: akun } = await supabase
          .from("akun")
          .select("role")
          .eq("id", session.user.id)
          .single();
        if (akun) role = akun.role;
      }

      const allowedRoles = [
        "operator",
        "admin",
        "administrator",
        "kepala",
        "kepala_sekolah",
        "tutor",
      ];
      setCanEdit(allowedRoles.includes(role.toLowerCase()));

      // 2. Ambil Mapel
      const { data: dataMapel } = await supabase
        .from("mata_pelajaran")
        .select("*")
        .order("nama_mapel");
      setMapelList(dataMapel || []);

      // 3. Ambil Materi (Urutkan sesuai urutan drag-drop)
      // Pastikan select mengambil semua kolom (*) termasuk 'kelas'
      const { data: dataMateri, error: errMateri } = await supabase
        .from("materi")
        .select("*")
        .order("urutan", { ascending: true })
        .order("created_at", { ascending: false });

      if (errMateri) throw new Error(errMateri.message);

      setMateriList(dataMateri || []);
    } catch (err) {
      showToast(`Gagal memuat: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  }

  // --- LOGIKA DRAG AND DROP ---
  const handleOnDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(materiList);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setMateriList(items);

    try {
      const updates = items.map((item, index) => ({
        id: item.id,
        urutan: index + 1,
        judul: item.judul,
        link_url: item.link_url,
        mata_pelajaran: item.mata_pelajaran,
        jenis: item.jenis,
        is_public: item.is_public,
        kelas: item.kelas, // Pastikan kelas ikut tersimpan saat reorder
      }));

      const { error } = await supabase.from("materi").upsert(updates);
      if (error) throw error;
    } catch (error) {
      showToast("Gagal menyimpan urutan: " + error.message, "error");
      fetchInitialData();
    }
  };

  // --- CRUD FUNCTIONS ---
  async function handleSimpan(e) {
    e.preventDefault();
    if (!formData.judul || !formData.mata_pelajaran) {
      showToast("Judul dan Mapel wajib diisi!", "error");
      return;
    }

    try {
      const lastOrder =
        materiList.length > 0
          ? Math.max(...materiList.map((m) => m.urutan || 0))
          : 0;

      const payload = {
        judul: formData.judul,
        link_url: formData.link_url,
        mata_pelajaran: formData.mata_pelajaran,
        jenis: formData.jenis,
        is_public: formData.is_public === "true",
        kelas: formData.kelas || null, // TAMBAHAN: Simpan Kelas
        urutan: isEditing ? undefined : lastOrder + 1,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("materi")
          .update(payload)
          .eq("id", editId);
        if (error) throw error;
        showToast("Materi diperbarui!", "success");
      } else {
        const { error } = await supabase.from("materi").insert([payload]);
        if (error) throw error;
        showToast("Materi ditambahkan!", "success");
      }

      setFormData({
        judul: "",
        link_url: "",
        mata_pelajaran: "",
        jenis: "Materi",
        is_public: "true",
        kelas: "", // Reset kelas
      });
      setIsEditing(false);
      setEditId(null);
      fetchInitialData();
    } catch (error) {
      showToast("Gagal simpan: " + error.message, "error");
    }
  }

  function clickEdit(item) {
    setFormData({
      judul: item.judul,
      link_url: item.link_url || "",
      mata_pelajaran: item.mata_pelajaran,
      jenis: item.jenis || "Materi",
      is_public: item.is_public ? "true" : "false",
      kelas: item.kelas || "", // Isi form dengan data kelas yang ada
    });
    setIsEditing(true);
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() {
    setFormData({
      judul: "",
      link_url: "",
      mata_pelajaran: "",
      jenis: "Materi",
      is_public: "true",
      kelas: "",
    });
    setIsEditing(false);
    setEditId(null);
  }

  function clickHapus(item) {
    setDeleteModal({ show: true, id: item.id, title: item.judul });
  }

  async function confirmHapus() {
    const { error } = await supabase
      .from("materi")
      .delete()
      .eq("id", deleteModal.id);
    setDeleteModal({ show: false, id: null, title: "" });

    if (error) showToast("Gagal menghapus.", "error");
    else {
      showToast("Materi dihapus.", "success");
      fetchInitialData();
    }
  }

  const getBadge = (jenis) => {
    const safeJenis = jenis || "Materi";
    if (safeJenis === "Latihan")
      return {
        bg: "#dcfce7",
        text: "#166534",
        icon: <CheckCircle size={13} />,
        label: "Latihan",
      };
    return {
      bg: "#dbeafe",
      text: "#1e40af",
      icon: <BookOpen size={13} />,
      label: "Materi",
    };
  };

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#2563eb" />
      </div>
    );

  const toastStyle = {
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    top: notification.show ? "30px" : "-80px",
    opacity: notification.show ? 1 : 0,
    background: "white",
    padding: "12px 24px",
    borderRadius: "50px",
    boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    zIndex: 9999,
    border:
      notification.type === "success"
        ? "1px solid #dcfce7"
        : "1px solid #fee2e2",
    transition: "all 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    opacity: deleteModal.show ? 1 : 0,
    visibility: deleteModal.show ? "visible" : "hidden",
    transition: "all 0.2s ease-in-out",
  };

  return (
    <div
      style={{
        maxWidth: "800px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <div style={toastStyle}>
        {notification.type === "success" ? (
          <CheckCircle size={20} color="#22c55e" />
        ) : (
          <AlertCircle size={20} color="#ef4444" />
        )}
        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
          {notification.message}
        </span>
      </div>

      <div style={modalOverlayStyle}>
        <div
          style={{
            background: "white",
            padding: "30px",
            borderRadius: "20px",
            width: "90%",
            maxWidth: "350px",
            textAlign: "center",
            transform: deleteModal.show ? "scale(1)" : "scale(0.95)",
            transition: "all 0.2s",
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              background: "#FEF2F2",
              borderRadius: "50%",
              margin: "0 auto 20px auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash2 size={30} color="#EF4444" />
          </div>
          <h3 style={{ margin: "0 0 10px 0", color: "#1E293B" }}>
            Hapus Materi?
          </h3>
          <p style={{ color: "#64748B", fontSize: "14px", marginBottom: 25 }}>
            "{deleteModal.title}" akan dihapus permanen.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setDeleteModal({ show: false })}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "white",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Batal
            </button>
            <button
              onClick={confirmHapus}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "none",
                background: "#EF4444",
                color: "white",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Hapus
            </button>
          </div>
        </div>
      </div>

      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748b",
          marginBottom: "20px",
        }}
      >
        <ArrowLeft size={18} /> Kembali
      </Link>

      {/* FORM INPUT */}
      {canEdit && (
        <div
          style={{
            background: "white",
            padding: "30px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
            marginBottom: "30px",
            border: isEditing ? "2px solid #f59e0b" : "1px solid #e2e8f0",
          }}
        >
          <h2
            style={{
              marginTop: 0,
              color: "#1e293b",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            {isEditing ? <Edit3 color="#f59e0b" /> : <Video color="#f59e0b" />}
            {isEditing ? "Edit Materi" : "Upload Materi Baru"}
          </h2>
          <form onSubmit={handleSimpan}>
            <div style={{ marginBottom: "15px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Judul Materi
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                placeholder="Contoh: Rumus Phytagoras"
                value={formData.judul}
                onChange={(e) =>
                  setFormData({ ...formData, judul: e.target.value })
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "15px",
                marginBottom: "15px",
                flexWrap: "wrap",
              }}
            >
              {/* MAPEL */}
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Mata Pelajaran
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    background: "white",
                  }}
                  value={formData.mata_pelajaran}
                  onChange={(e) =>
                    setFormData({ ...formData, mata_pelajaran: e.target.value })
                  }
                >
                  <option value="">-- Pilih Mapel --</option>
                  {mapelList.map((mp) => (
                    <option key={mp.id} value={mp.nama_mapel}>
                      {mp.nama_mapel}
                    </option>
                  ))}
                </select>
              </div>

              {/* TAMBAHAN: PILIH KELAS */}
              <div style={{ flex: 1, minWidth: "150px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Kelas / Tingkat
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    background: "white",
                  }}
                  value={formData.kelas}
                  onChange={(e) =>
                    setFormData({ ...formData, kelas: e.target.value })
                  }
                >
                  <option value="">-- Semua Kelas --</option>
                  {opsiKelas.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>

              {/* JENIS KONTEN */}
              <div style={{ flex: 1, minWidth: "150px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Jenis Konten
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    background: "white",
                  }}
                  value={formData.jenis}
                  onChange={(e) =>
                    setFormData({ ...formData, jenis: e.target.value })
                  }
                >
                  <option value="Materi">📖 Materi Belajar</option>
                  <option value="Latihan">📝 Latihan Soal</option>
                </select>
              </div>

              {/* AKSES PREMIUM/GRATIS */}
              <div style={{ flex: 1, minWidth: "150px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                    color: "#64748b",
                    textTransform: "uppercase",
                  }}
                >
                  Akses Materi
                </label>
                <select
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    background: "white",
                    color:
                      formData.is_public === "true" ? "#166534" : "#b45309",
                    fontWeight: "bold",
                  }}
                  value={formData.is_public}
                  onChange={(e) =>
                    setFormData({ ...formData, is_public: e.target.value })
                  }
                >
                  <option value="true">🌍 Gratis (Public)</option>
                  <option value="false">🔒 Premium (Berbayar)</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "25px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Link Sumber
              </label>
              <div style={{ position: "relative" }}>
                <IconLink
                  size={18}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: 12,
                    color: "#94a3b8",
                  }}
                />
                <input
                  style={{
                    width: "100%",
                    padding: "12px 12px 12px 40px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                  placeholder="https://youtube.com/..."
                  value={formData.link_url}
                  onChange={(e) =>
                    setFormData({ ...formData, link_url: e.target.value })
                  }
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="submit"
                style={{
                  flex: 1,
                  background: isEditing ? "#f59e0b" : "#2563eb",
                  color: "white",
                  padding: "14px",
                  borderRadius: "8px",
                  border: "none",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Save size={18} />{" "}
                {isEditing ? "Update Materi" : "Simpan ke Database"}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  style={{
                    width: "100px",
                    background: "#94a3b8",
                    color: "white",
                    padding: "14px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  Batal
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* --- LIST MATERI --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <h3 style={{ color: "#334155", margin: 0 }}>
          Daftar Pustaka ({materiList.length})
        </h3>
        {canEdit && (
          <span style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
            *Drag untuk urutkan
          </span>
        )}
      </div>

      {materiList.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px",
            color: "#94a3b8",
            background: "white",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <BookOpen size={40} style={{ opacity: 0.3, marginBottom: 10 }} />
          <p>Belum ada materi.</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="materi-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ display: "grid", gap: "15px" }}
              >
                {materiList.map((item, index) => {
                  const safeJenis = item.jenis || "Materi";
                  const safeMapel = item.mata_pelajaran || "Umum";
                  const badge = getBadge(safeJenis);
                  const isPremium = item.is_public === false;

                  // CEK KELAS
                  const kelasLabel = item.kelas || "Umum";

                  return (
                    <Draggable
                      key={item.id.toString()}
                      draggableId={item.id.toString()}
                      index={index}
                      isDragDisabled={!canEdit}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            background: "white",
                            padding: "15px",
                            borderRadius: "12px",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            boxShadow: snapshot.isDragging
                              ? "0 10px 20px rgba(0,0,0,0.1)"
                              : "0 2px 4px rgba(0,0,0,0.02)",
                            position: "relative",
                            overflow: "hidden",
                            ...provided.draggableProps.style,
                          }}
                        >
                          {/* DRAG HANDLE */}
                          {canEdit && (
                            <div
                              {...provided.dragHandleProps}
                              style={{
                                marginRight: 15,
                                cursor: "grab",
                                color: "#cbd5e1",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <GripVertical size={20} />
                            </div>
                          )}

                          <div
                            style={{
                              position: "absolute",
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: 4,
                              background: badge.text,
                            }}
                          ></div>

                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                marginBottom: "4px",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  background: badge.bg,
                                  color: badge.text,
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {badge.icon} {badge.label}
                              </span>

                              {/* BADGE KELAS (BARU) */}
                              <span
                                style={{
                                  background: "#f3e8ff",
                                  color: "#7e22ce",
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Layers size={10} /> {kelasLabel}
                              </span>

                              <span
                                style={{
                                  background: isPremium ? "#fff7ed" : "#f0fdf4",
                                  color: isPremium ? "#b45309" : "#166534",
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {isPremium ? (
                                  <Lock size={10} />
                                ) : (
                                  <Globe size={10} />
                                )}
                                {isPremium ? "Premium" : "Gratis"}
                              </span>

                              <span
                                style={{
                                  background: "#f1f5f9",
                                  color: "#475569",
                                  padding: "2px 8px",
                                  borderRadius: "20px",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Tag size={12} /> {safeMapel}
                              </span>
                            </div>

                            <h4
                              style={{
                                margin: "0 0 2px 0",
                                color: "#1e293b",
                                fontSize: "15px",
                              }}
                            >
                              {item.judul}
                            </h4>

                            {item.link_url ? (
                              <a
                                href={item.link_url}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  fontSize: "12px",
                                  color: "#2563eb",
                                  textDecoration: "none",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "5px",
                                }}
                              >
                                <IconLink size={12} /> Buka Tautan
                              </a>
                            ) : (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#94a3b8",
                                  fontStyle: "italic",
                                }}
                              >
                                Tidak ada link.
                              </span>
                            )}
                          </div>

                          {canEdit && (
                            <div
                              style={{
                                display: "flex",
                                gap: 5,
                                marginLeft: 10,
                              }}
                            >
                              <button
                                onClick={() => clickEdit(item)}
                                style={{
                                  background: "#fef3c7",
                                  color: "#d97706",
                                  border: "none",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                }}
                                title="Edit"
                              >
                                <Edit3 size={16} />
                              </button>
                              <button
                                onClick={() => clickHapus(item)}
                                style={{
                                  background: "#fff1f2",
                                  color: "#be123c",
                                  border: "none",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                }}
                                title="Hapus"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
}
